type ExpressionFunction<E> = () => E;
type Expression<E> = E | ExpressionFunction<E>;
type Predicate<P> = P | ((p: P) => boolean);

interface MatchOptions {
  /**  If we return the default value when exhausted, match will print a warning.
  Default to `true`.*/
  log: boolean;
  /** If we return the default value when exhausted, match will throw an exception.
  Default to `false` */
  throw: boolean;
}

interface Payload<T, E> {
  value: T;
  expression?: Expression<E>;
  defaultValue?: Expression<E>;
  options: MatchOptions;
}

interface Matcher<T, E, SoFar extends T = never> {
  case<P extends T, K>(
    predicate: Predicate<P>,
    expression: Expression<K>
  ): Matcher<T, E | K, SoFar | P>;
  default<K>(expression: Expression<K>): {
    get(): E | K;
    exhaustive(
      ...args: [T] extends [SoFar]
        ? []
        : [error: ["🚨 you forgot the case for:", Exclude<T, SoFar>]]
    ): E | K;
  };
}

const DEFAULT_MATCH_OPTIONS: MatchOptions = {
  log: true,
  throw: false,
};

/**
 * `lxSwitch` is a `switch` on steroids.
 *
 * ### Usage
 *
 * ```js
 * const color = lxSwitch(variant)
 *                .case('primary', 'red') // matches primitive values
 *                                        // returns `red` if `variant` === `primary`
 *                .case(/secondary/, 'blue') // matches RegExp
 *                                           // returns `blue` if `variant` contains `secondary`
 *                .case((v) => v.startsWith('lx'), 'green') // functions matchers
 *                .case('random', () => randomColor() ) // functions as return value
 *                .default('black') // .default is always required.
 *                                  // You may also throw an error here or use `null/undefined`
 *                .get()
 *                // `.get()` or `.exhaustive()` to get the value.
 *                //
 *                // Use `.exhaustive()` if  `variant` would be a union type,
 *                // e.g `type Variant = "primary" | "secondary"`.
 *                // The compiler would complain if you miss a case.
 *                //
 *                // `.exhaustive()` is not useful
 *                // if you are using primitives like `string` or `number`
 *```
 *
 *  ### JSX
 *
 *  To branch code (if-else) for more than two options is in React/JSX
 *  not straightforward. The only way to do it is to use  _Immediately Invoked Function Expressions (IIFE)_
 *  or nested ternaries. Nested ternaries are hard to read and harder to type.
 *  Also, there is no exhaustive check.
 *
 *  Here is how to branch in JSX using `lxSwitch`:
 *
 *```js
 *  const {status, data} = useQuery(...)
 *
 *  <div>
 *    {
 *    lxSwitch(status)
 *      .case('loading', () => <p>Loading...</p>)
 *      .case('success', () => <MyData data={data}/>)
 *      .case('error', () => <p>Oops, an error occurred</p>)
 *      .default(() => <p>Oops, an unknown case occurred.</p>)
 *      .exhaustive() // Compiler check if we forgot a case
 *    }
 *  </div>;
 *```
 * ### Options
 *
 * You can pass options to `lxSwitch` to control the behavior of `.exhaustive()`
 *
 * Normally, `.exhaustive()` will not return the default value since all cases
 * are covered. If the default value is returned it is a sign that you forgot
 * a case in your typings. This may happen if you are consuming data send form the server
 * and you forget to add a case for the data you receive. In this case, the `default`
 * is used as a fallback and safety mechanism so your code will not break in production.
 *
 * However, if you are using `.exhaustive()` in development environment,
 * you may want `lxSwitch` to throw an error so your E2E-tests will break early and
 * the hard way.
 *
 * Options are:
 *
 * - `throw`: throw an exception if the default value is returned. Default to `false`
 * - `log`: log an error if the default value is returned. Default to `true`
 *
 *
 * ```js
 * const isDev = true;
 * lxSwitch(variant, { throw: isDev, log:true})
 *          .case('primary', 'red')
 *          .default('black')
 *          .exhaustive()
 * ```
 *
 * Note: this does not apply to `.get()`
 *
 * ### Background
 *
 * This is in a sense a _pattern matching_ function. However it is tiny and does not
 * have a lot of features.
 *
 * There is a proposal to bring pattern matching to Javascript in
 * the future: [TC39 Pattern Matching Proposal](https://github.com/tc39/proposal-pattern-matching).
 *
 * If you want to use a full-blown pattern matching library today, you may have a look
 * at [Pattern Matching](https://github.com/gvergnaud/ts-pattern)
 *
 * You may also find this blog post useful:
 * [Bringing Pattern Matching to TypeScript](https://dev.to/gvergnaud/bringing-pattern-matching-to-typescript-introducing-ts-pattern-v3-0-o1k)
 *
 * @package Utils
 * @example
 * // Using primitive types, like strings, numbers, booleans, etc.:
 *  const emoji = lxSwitch(statusCode)
 *                 .case(200, '🎉')
 *                 .case(404, '💩')
 *                 .default('🤷')
 *                 .get()
 *
 *  // Using Union types:
 *  type Status = 'loading' | 'success' | 'error' | 'idle'
 *  const emoji = lxSwitch(status)
 *                 .case('loading', '⏳')
 *                 .case('success', '🎉')
 *                 .case('error', '💩')
 *                 .default('🤷')
 *                 .exhaustive() // complains if you miss a case, in this example `idle`
 *
 *  // Using function:
 *  const isOdd = (n: number) => n % 2 === 1;
 *  const isEven = (n: number) => n % 2 === 0;
 *  const isNull = (n: number) => n === 0;
 *  const emoji = lxSwitch(myNumber)
 *                 .case(isNull, '💩')
 *                 .case(isOdd, '🎉')
 *                 .case(isEven, '🥳')
 *                 .default('🤷')
 *                 .get()
 *
 * // Using RegExp:
 * const emoji = lxSwitch<string | RegExp>(message)
 *                 .case(/^OK/, '🎉') // Message starts with `OK`
 *                 .case(/Error/, '💩') // Message contains `Error`
 *                 .default('🤷')
 *                 .get()
 *
 * // Using in JSX:
 * <div>
 *  {
 *    lxSwitch(status)
 *      .case('loading', () => <p>Loading...</p>)
 *      .case('success', () => <p>{data}</p>)
 *      .case('error', () => <p>Oops, an error occurred</p>)
 *      .default(() => <p>Oops, an unknown case occurred.</p>)
 *      .exhaustive() // <- this checks exhaustiveness and returns the matched value
 *    }
 *  </div>;
 */
export function lxSwitch<T>(t: T, options?: Partial<MatchOptions>) {
  return Left<T>({
    value: t,
    expression: undefined,
    defaultValue: undefined,
    options: {
      ...DEFAULT_MATCH_OPTIONS,
      ...options,
    },
  });
}

function Left<T, E = never, SoFar extends T = never>(
  payload: Payload<T, E>
): Matcher<T, E, SoFar> {
  return {
    case: <P extends T, K>(pred: Predicate<P>, exp: Expression<K>) => {
      let match = false; // would be nice if we could use `lxSwitch` here ;)
      if (isRegExp(pred)) {
        match = pred.test("" + payload.value);
      } else if (isFunction(pred)) {
        match = pred(payload.value as any);
      } else {
        match = payload.value === pred;
      }
      if (match) {
        return Right<T, E | K, SoFar | P>({
          ...payload,
          expression: exp,
        });
      }
      return Left<T, E | K, SoFar | P>(payload);
    },
    default: (defaultExpression) => ({
      get: () => {
        return evaluate(defaultExpression);
      },
      exhaustive: (
        ..._args: [T] extends [SoFar]
          ? []
          : [error: ["🚨 you forgot the case for:", T]]
      ) => {
        const msg = `🚨 match: we are exhausted, but we return the default value. Hence, your union type is not comprehensive.`;
        if (payload.options.log) {
          console.error(msg);
        }
        if (payload.options.throw) {
          throw new Error(msg);
        }
        return evaluate(defaultExpression);
      },
    }),
  };
}

function Right<T, E = never, SoFar extends T = never>(
  payload: Payload<T, E>
): Matcher<T, E, SoFar> {
  return {
    case: <P extends T, K>(_pred: Predicate<P>, _exp: Expression<K>) => {
      return Right(payload);
    },
    default: (_defaultExpression) => ({
      get: () => {
        return evaluate(payload.expression!);
      },
      exhaustive: (
        ..._args: [T] extends [SoFar]
          ? []
          : [error: ["🚨 you forgot the case for:", T]]
      ) => {
        return evaluate(payload.expression!);
      },
    }),
  };
}

//
// Type-Guards
//
function isFunction(value: unknown): value is Function {
  return typeof value === "function";
}

function isRegExp(value: unknown): value is RegExp {
  return value instanceof RegExp;
}

function evaluate<E>(expression: Expression<E>): E {
  if (isFunction(expression)) {
    return expression();
  }
  return expression;
}
